import {
    Dialog, DialogContent, DialogHeader,
    DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/Modal";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import JobForm from "./component/JobForm";
import Skeleton from "@/components/shared/Skeleton";
import { JobService } from "@/services/jobService";
import { parseArray } from "@/utils/parseArray";
import { toLanguageRows } from "@/utils/languageRequirements";
import { parseShiftRange, to24HourDisplay } from "@/utils/shiftTime";
import { shortId } from "@/utils/formatText";

// Placeholder rows only — the count and the two-column grid match the form's
// first step, nothing more depends on the names.
const FORM_SKELETON_FIELDS = ["title", "deadline", "location", "employeeType", "shift", "vacancies", "experienceMin", "experienceMax", "priority"];

/**
 * CloneJobForm
 *
 * Prefills the create form with source-job data then submits via
 * POST /api/v2/job/create with cloned_from_job_id.
 *
 * Clone-specific differences vs EditJobForm:
 *   • id is dropped        → form enters create mode (currency not locked)
 *   • deadline is cleared  → past deadlines cause 422; force user to pick a new one
 *   • assignedTo cleared   → old assignee may be deactivated / out of scope
 *   • mandatoryFields / selectedConfigurable are cleared
 *                          → always start blank, same as a fresh create
 */
const CloneJobForm = ({
    open,
    jobId,          // source job whose data is prefilled
    onClose,
    onSuccess,      // (newJobId) => void
    userOptions,
}) => {
    const navigate = useNavigate();
    const [defaultValues, setDefaultValues] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sourceTitle, setSourceTitle] = useState("");

    useEffect(() => {
        if (open && jobId) {
            setDefaultValues(null);
            setSourceTitle("");

            const fetchAndMap = async () => {
                try {
                    const data = await JobService.fetchJobById(jobId);
                    const job = data.data;

                    setSourceTitle(job.title || "");

                    // A source job that predates structured shift timing has
                    // only the free-text string, and the clone has nowhere to
                    // put it — the payload carries structured fields only, so
                    // the clone used to come out with no shift at all while
                    // the form said one was set. Read the hours out of the
                    // text into a custom shift instead. The time zone is left
                    // for the user: the text carries an abbreviation ("UTC",
                    // "IST"), and that is not an IANA zone name.
                    const legacyShift = job.shift_type ? null : parseShiftRange(job.shift_timing);
                    const carriesLegacyShift = !!legacyShift?.startTime;

                    setDefaultValues({
                        // ── id intentionally omitted → create mode ──
                        jobTitle: job.title || "",
                        deadline: "",               // always clear — past deadline → 422
                        location: job.location || "",
                        employeeType: job.employee_type || "",
                        // Carried over into the clone as an independent copy —
                        // a predefined selection is re-validated (active,
                        // same tenant) on submit, not linked back to the
                        // source job; a custom shift's values are copied as
                        // plain scalars, never a reference to the source.
                        shiftTiming: carriesLegacyShift ? "" : job.shift_timing || "",
                        shiftType: carriesLegacyShift ? "custom" : job.shift_type || "",
                        predefinedShiftId: job.predefined_shift_id || "",
                        shiftStartTime: carriesLegacyShift ? legacyShift.startTime : to24HourDisplay(job.shift_start_time),
                        shiftEndTime: carriesLegacyShift ? legacyShift.endTime : to24HourDisplay(job.shift_end_time),
                        // Blank on the legacy path — deliberately. See above.
                        shiftTimezone: job.shift_timezone || "",
                        calendarTypeId: job.calendar_type_id || "",
                        vacancyCount: job.vacancy_count || "",
                        experienceMin: job.experience_min ?? "",
                        experienceMax: job.experience_max ?? "",
                        overview: job.role_overview || "",
                        responsibility: job.key_responsibilities || "",
                        department: job.department || "",
                        division: job.division || "",
                        companyWebsite: job.company_link || "",
                        currency: job.currency || "",
                        currency_sign: job.currency_sign || "",
                        linkedIn: job.company_linkedin_link || "",
                        niceToHave: parseArray(job.nice_to_have),
                        certifications: parseArray(job.certifications),
                        languagesRequired: toLanguageRows(job.languages_required),
                        skills: job.skills || [],
                        interviewers: job.interviewers || [],
                        jobPriority: job.job_priority ?? "",
                        // ── clear assignment — old assignee may be deactivated ──
                        assignedTo: "",
                        // ── field config always starts blank (same as fresh create) ──
                        mandatoryFields: [],
                        selectedConfigurable: [],
                        selectedInterviewTypes: [],
                        selectedBooleanPlatforms: [],
                        // ── the three email-consent flows are deliberately absent ──
                        // Decision 4: a clone starts from the org default at
                        // clone time, not from the source job's answers. Leaving
                        // the keys off entirely is what makes that happen —
                        // ConsentConfigSelector pre-sets any field that is still
                        // `undefined`. Copying `job.require_*_email_consent` here
                        // would carry the source job's values across instead.
                        // Copy the source job's questions WITHOUT their ids, so
                        // the clone inserts brand-new rows — editing the clone
                        // never touches the original, and vice versa.
                        applicationQuestions: (job.application_questions || []).map((q) => ({
                            question: q.question,
                            answerType: q.answer_type,
                            isRequired: q.is_required,
                        })),
                    });
                } catch (error) {
                    console.error("Failed to fetch source job for cloning:", error);
                    toast.error("Failed to load job data.", { position: "bottom-right" });
                    onClose();
                }
            };

            fetchAndMap();
        }
    }, [open, jobId]);

    const handleCloneSubmit = async (formData) => {
        try {
            setIsSubmitting(true);
            const result = await JobService.cloneJob(jobId, formData);
            const newJobId = result?.data?.id;
            toast.success("Job cloned successfully.", { position: "bottom-right" });
            onSuccess?.(newJobId);
            onClose();
        } catch (error) {
            if (error?.isDuplicate) {
                const { duplicate_job_id, duplicate_job_title, duplicate_job_status } =
                    error.duplicateData || {};
                const status = duplicate_job_status
                    ? ` (${duplicate_job_status.charAt(0) + duplicate_job_status.slice(1).toLowerCase()})`
                    : "";

                toast(
                    (t) => (
                        <span className="text-sm">
                            An identical job already exists —{" "}
                            <strong>{duplicate_job_title || "this job"}</strong>
                            {duplicate_job_id && (
                                <span className="text-text-secondary font-mono text-xs"> ({shortId(duplicate_job_id)})</span>
                            )}
                            {status}.{" "}
                            {duplicate_job_id && (
                                <Button
                                    onClick={() => {
                                        toast.dismiss(t.id);
                                        // The clone dialog must close here — it stayed
                                        // open behind the toast before, so navigating
                                        // to the existing job rendered underneath it
                                        // and looked like the link did nothing.
                                        onClose();
                                        navigate(`/admin/job-detail/${duplicate_job_id}`);
                                    }}
                                    className="p-0 rounded-none underline text-tone-blue hover:opacity-80"
                                >
                                    Open existing job →
                                </Button>
                            )}
                        </span>
                    ),
                    {
                        position: "bottom-right",
                        duration: 8000,
                        icon: "⚠️",
                    }
                );
                // keep modal open so user can change a field and retry
                return;
            }

            const message =
                error?.response?.data?.message ||
                error?.response?.data?.detail ||
                error?.message ||
                "Failed to clone job.";
            toast.error(message, { position: "bottom-right" });
            console.error("Failed to clone job:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) onClose();
            }}
        >
            <DialogContent className="h-[88vh] w-[340px] md:w-full flex flex-col">
                <DialogHeader
                    className="relative px-5 py-3 border-b border-border-primary shrink-0"
                    style={{ zoom: 0.95 }}
                >
                    <div>
                        <DialogTitle className="text-[15px] font-semibold text-text-primary">
                            Clone Job Posting
                        </DialogTitle>
                        <DialogDescription className="text-[12px] text-text-primary/70 mt-0.5">
                            {sourceTitle ? (
                                <>
                                    Prefilled from · {sourceTitle}
                                    {jobId && <span className="font-mono opacity-70"> ({shortId(jobId)})</span>}
                                </>
                            ) : (
                                "Loading source job…"
                            )}
                        </DialogDescription>
                    </div>
                    <DialogClose className="absolute right-5 top-1/2 -translate-y-1/2">
                        <span className="w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center text-text-primary hover:bg-background-icon/60 transition-colors">
                            <X size={14} strokeWidth={3} />
                        </span>
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="px-5 py-3" style={{ zoom: 0.95 }}>
                        {defaultValues ? (
                            <JobForm
                                key={jobId}
                                onClose={onClose}
                                loadingLabel="Creating…"
                                onSubmit={handleCloneSubmit}
                                defaultValues={defaultValues}
                                submitLabel="Create Cloned Job"
                                isSubmitting={isSubmitting}
                                userOptions={userOptions}
                            />
                        ) : (
                            /* The source job is a network round-trip away, and
                               without this the modal was a header over 88vh of
                               nothing. The skeleton has the shape of the form's
                               first step, so the panel does not jump when the
                               real fields arrive. */
                            <div className="flex flex-col gap-5" aria-busy="true">
                                <Skeleton className="h-9 w-full" />
                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    {FORM_SKELETON_FIELDS.map((field) => (
                                        <div key={field} className="flex flex-col gap-2">
                                            <Skeleton className="h-3.5 w-28" />
                                            <Skeleton className="h-10 w-full" />
                                        </div>
                                    ))}
                                </div>
                                <Skeleton className="h-3.5 w-32" />
                                <Skeleton className="h-28 w-full" />
                                <div className="flex justify-end gap-3">
                                    <Skeleton className="h-10 w-24" />
                                    <Skeleton className="h-10 w-36" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CloneJobForm;
